import { useEffect, useRef, useState, type ReactNode } from 'react';
import './ArtSplash.css';

/** Shared presentation; callers still own all session/readiness decisions. */
export function ArtSplash({
  paused = false,
  headingAs: Heading = 'h2',
  headingId,
  accessibleHeading,
  markClassName,
  children,
}: {
  paused?: boolean;
  headingAs?: 'h1' | 'h2';
  headingId?: string;
  accessibleHeading?: string;
  markClassName?: string;
  children?: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(true);
  const [foreground, setForeground] = useState(true);
  useEffect(() => {
    const visibility = () => setForeground(document.visibilityState !== 'hidden');
    visibility();
    document.addEventListener('visibilitychange', visibility);
    const observer =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(([entry]) => setVisible(Boolean(entry?.isIntersecting)), {
            threshold: 0.01,
          });
    if (observer && ref.current) observer.observe(ref.current);
    else setVisible(true);
    return () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', visibility);
    };
  }, []);
  return (
    <section
      ref={ref}
      className="art-splash"
      aria-label={accessibleHeading ?? 'Art splash concept'}
      data-paused={paused || !visible || !foreground}
    >
      <div className="art-splash__field art-splash__field--blue" aria-hidden="true" />
      <div className="art-splash__field art-splash__field--violet" aria-hidden="true" />
      <div className="art-splash__light" aria-hidden="true" />
      <div className="art-splash__content">
        <img className={markClassName} src="/icons/admin-heart.svg" width="80" height="80" alt="" />
        <Heading id={headingId} aria-label={accessibleHeading}>
          Perova Photo
        </Heading>
      </div>
      {children}
    </section>
  );
}
